import { memo, useRef, useEffect, useCallback, useState } from "react";
import { Download, Eye, Loader2, Plus, Check, FileText } from "lucide-react";
import Button from "@mui/material/Button";
import TextField from "@/components/common/GoogleTextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useAuthStore } from "@/stores/auth";
import { useThemeStore } from "@/stores/theme";
import { downloadReport } from "@/api/chat";
import { extractTitle, triggerDownload } from "../utils/reportUtils";
import { FileBubble } from "./FileBubble";
import { ChartRenderer } from "@/components/charts/ChartRenderer";
import { useAddDashboardWidget } from "@/features/agents/hooks/useAgents";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { contextLabel, contextualElementsLabel } from "../lib/contextLabels";
import type { ChatMessage, MessageSummary, ChartSpec } from "@/types";
import { FieldLabel } from "@/components/shared/FieldLabel";
import { isLiveCapableChart } from "@/features/dashboard/hooks/useWidgetLiveSpec";

const SAMPLE_QUESTIONS = [
  "How are my company's emissions trending this quarter?",
  "What are the GRI disclosure requirements for emissions?",
  "Help me generate a sustainability report",
  "What are the latest trends in carbon accounting?",
];
const PROSE_CLASSES =
  "max-w-full min-w-0 break-words overflow-hidden " +
  "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 " +
  "[&_ul]:my-2 [&_ol]:my-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 " +
  "[&_code]:bg-muted [&_code]:px-[0.4rem] [&_code]:py-[0.2rem] [&_code]:rounded-[3px] [&_code]:text-sm " +
  "[&_pre]:bg-muted [&_pre]:p-3 [&_pre]:rounded-sm [&_pre]:overflow-x-auto [&_pre]:max-w-full " +
  "[&_table]:border-collapse [&_table]:my-2 [&_table]:w-full [&_table]:table-fixed [&_table]:max-w-full " +
  "[&_th]:border [&_td]:border " +
  "[&_th]:border-border [&_td]:border-border [&_th]:p-2 [&_td]:p-2 [&_th]:text-left [&_td]:text-left " +
  "[&_th]:bg-muted [&_th]:font-semibold [&_th]:text-xs " +
  "[&_td]:break-words [&_th]:break-words [&_td]:max-w-full [&_td]:overflow-hidden " +
  "[&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-2 " +
  "[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1 " +
  "[&_img]:max-w-full [&_img]:h-auto " +
  "[&_a]:inline-flex [&_a]:items-center [&_a]:max-w-full [&_a]:py-1.5 [&_a]:bg-primary/10 [&_a]:text-primary " +
  "[&_a]:px-1.5 [&_a]:rounded-full [&_a]:no-underline [&_a]:whitespace-normal [&_a]:break-all [&_a:hover]:bg-primary [&_a:hover]:text-primary-foreground";

interface MessageBubbleProps {
  messages: ChatMessage[];
  loading: boolean;
  currentProgress?: string | null;
  isThinking: boolean;
  streamingContent?: string | null;
  onSampleClick?: (question: string) => void;
  onReviewClick?: (batchId: string, recordCount: number) => void;
  onEmissionReviewClick?: (chatId: string, messageId: number) => void;
  onSummaryClick?: (summary: MessageSummary) => void;
  compact?: boolean;
  /**
   * Empty-state prompt chips. Supplied by the caller so they can follow the
   * active page (see `getSuggestedQuestions`); falls back to the account-wide
   * questions when omitted.
   */
  suggestedQuestions?: string[];
}

function ThinkingIndicator({
  currentProgress,
}: {
  currentProgress?: string | null;
}) {
  return (
    <div
      className="flex gap-3 self-start justify-start animate-fade-in"
      role="status"
      aria-label="AI is thinking"
    >
      <div className="p-3.5 flex-1 bg-card text-card-foreground border border-border rounded-xl rounded-bl-sm">
        <div className="flex items-center gap-2.5">
          {currentProgress ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm font-medium">{currentProgress}</span>
            </>
          ) : (
            <>
              <div className="flex gap-[3px] items-center">
                <span className="h-[5px] w-[5px] rounded-full bg-primary animate-[thinking_1.4s_ease-in-out_infinite]" />
                <span className="h-[5px] w-[5px] rounded-full bg-primary animate-[thinking_1.4s_ease-in-out_0.2s_infinite]" />
                <span className="h-[5px] w-[5px] rounded-full bg-primary animate-[thinking_1.4s_ease-in-out_0.4s_infinite]" />
              </div>
              <span className="text-sm text-muted-foreground">Thinking...</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AddToDashboardButton({ spec }: { spec: ChartSpec }) {
  const addWidget = useAddDashboardWidget();
  const [saved, setSaved] = useState(false);

  if (saved) {
    return (
      <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-success">
        <Check className="h-3 w-3" />
        <span>Saved to dashboard</span>
      </div>
    );
  }

  return (
    <Button
      variant="text"
      size="small"
      className="mt-1.5"
      disabled={addWidget.isPending}
      onClick={() => {
        addWidget.mutate(
          { chart_spec: spec as unknown as Record<string, unknown> },
          {
            onSuccess: () => {
              setSaved(true);
              toast.success("Chart added to dashboard");
            },
            onError: () => {
              toast.error("Failed to add chart to dashboard");
            },
          },
        );
      }}
      sx={{
        color: "var(--muted-foreground)",
        "&:hover": {
          color: "var(--foreground)",
        },
      }}
    >
      <Plus className="h-3 w-3 mr-1" />
      Add to Dashboard
    </Button>
  );
}

export const MessageBubble = memo(function MessageBubble({
  messages,
  currentProgress,
  isThinking,
  streamingContent,
  onSampleClick,
  onReviewClick,
  onEmissionReviewClick,
  onSummaryClick,
  compact = false,
  suggestedQuestions,
}: MessageBubbleProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const followLatestRef = useRef(true);
  const token = useAuthStore((s) => s.token);
  const isDark = useThemeStore((s) => s.theme === "dark");
  const [downloading, setDownloading] = useState<string | null>(null);
  const [saveDialog, setSaveDialog] = useState<{
    content: string;
    ext: "pdf" | "docx";
    key: string;
    downloadFn: (
      content: string,
      title: string,
      token: string,
    ) => Promise<Blob>;
  } | null>(null);
  const [saveTitle, setSaveTitle] = useState("");
  const renderedMessages: ChatMessage[] = streamingContent
    ? [...messages, { role: "assistant", content: streamingContent }]
    : messages;

  useEffect(() => {
    if (messages.length === 0) followLatestRef.current = true;
    const scroller = scrollRef.current;
    if (!scroller || !followLatestRef.current) return;
    // Streaming updates must not restart a smooth scroll or move outer pages.
    scroller.scrollTop = scroller.scrollHeight;
  }, [messages, streamingContent, isThinking, currentProgress]);

  const handleDownload = useCallback(
    async (
      rawContent: string,
      ext: string,
      key: string,
      downloadFn: (
        content: string,
        title: string,
        token: string,
      ) => Promise<Blob>,
    ) => {
      if (!token) return;
      setSaveTitle(extractTitle(rawContent));
      setSaveDialog({
        content: rawContent,
        ext: ext as "pdf" | "docx",
        key,
        downloadFn,
      });
    },
    [token],
  );

  const confirmDownload = useCallback(async () => {
    if (!token || !saveDialog) return;
    const title = saveTitle.trim() || extractTitle(saveDialog.content);
    setDownloading(saveDialog.key);
    setSaveDialog(null);
    try {
      await triggerDownload(
        saveDialog.content,
        title,
        saveDialog.ext,
        saveDialog.downloadFn,
        token,
      );
    } catch {
      // silently fail
    } finally {
      setDownloading(null);
    }
  }, [token, saveDialog, saveTitle]);

  if (messages.length === 0 && !isThinking && !streamingContent) {
    return (
      <div
        className={cn(
          "flex-1 min-h-0 overflow-y-auto overflow-x-hidden",
          compact ? "px-3 py-4" : "px-4 py-5",
        )}
      >
        <div className="flex flex-col items-center justify-center gap-6 h-full px-5">
          <div className="relative">
            <div className="h-16 w-16 overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center p-2.5">
              <img
                src="/gm-logo.png"
                alt="Green Mirror"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="absolute -inset-4 rounded-3xl bg-primary/5 blur-xl -z-10" />
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
              How can I help you today?
            </h2>
            <p className="text-sm text-muted-foreground max-w-[360px] leading-relaxed">
              {suggestedQuestions
                ? "Ask about what you are working on, or pick one of the prompts below."
                : "Ask about emissions, targets, reports, or anything ESG related."}
            </p>
          </div>
          <div
            className="flex flex-wrap gap-2 justify-center max-w-[520px]"
            role="group"
            aria-label="Sample questions"
          >
            {(suggestedQuestions?.length
              ? suggestedQuestions
              : SAMPLE_QUESTIONS
            ).map((q, idx) => (
              <Button
                key={idx}
                variant="outlined"
                size="small"
                onClick={() => onSampleClick?.(q)}
                className="whitespace-normal break-words transition-[color,background-color,border-color,box-shadow,opacity,transform,translate,scale,rotate] duration-200 hover-lift"
                sx={{
                  height: "auto",
                  borderRadius: "9999px",
                  borderColor: "var(--border)",
                  color: "var(--muted-foreground)",
                  "&:hover": {
                    color: "var(--foreground)",
                    borderColor: "var(--primary-30)",
                    backgroundColor: "var(--primary-10)",
                  },
                }}
              >
                {q}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex-1 min-h-0 overflow-y-auto overflow-x-hidden",
        compact ? "px-3 py-4" : "px-4 py-5",
      )}
      role="log"
      ref={scrollRef}
      onScroll={(event) => {
        const node = event.currentTarget;
        followLatestRef.current =
          node.scrollHeight - node.scrollTop - node.clientHeight < 80;
      }}
      aria-label="Chat messages"
      aria-live="polite"
    >
      <div className="flex flex-col gap-4">
        {renderedMessages.map((msg, idx) => (
          <div
            key={`${idx}-${msg.role}`}
            className={cn(
              "animate-fade-in flex gap-3 min-w-0",
              msg.role === "user"
                ? "max-w-[85%] self-end justify-end flex-row-reverse"
                : "max-w-full justify-start",
            )}
          >
            <div
              className={cn(
                "p-3.5 leading-[1.5] flex-1 min-w-0",
                msg.role === "user"
                  ? "bg-primary text-primary-foreground rounded-xl rounded-br-sm"
                  : msg.isError
                    ? "bg-destructive/10 text-destructive border border-destructive/20 rounded-xl rounded-bl-sm"
                    : "bg-card text-card-foreground border border-border rounded-xl rounded-bl-sm",
              )}
            >
              {msg.role === "assistant" && !msg.isError ? (
                <>
                  <div
                    className={PROSE_CLASSES}
                    dangerouslySetInnerHTML={{ __html: msg.content }}
                  />
                  {msg.charts?.map((spec) => (
                    <div key={spec.id} className="mt-4 mb-2">
                      <ChartRenderer spec={spec} isDark={isDark} />
                      {isLiveCapableChart(spec) && (
                        <AddToDashboardButton spec={spec} />
                      )}
                    </div>
                  ))}
                  {msg.isReport && msg.rawContent && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          handleDownload(
                            msg.rawContent!,
                            "pdf",
                            `pdf-${idx}`,
                            (c, t, tk) => downloadReport(c, t, tk, "pdf"),
                          )
                        }
                        disabled={downloading === `pdf-${idx}`}
                      >
                        {downloading === `pdf-${idx}` ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="mr-2 h-4 w-4" />
                        )}
                        Download PDF
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          handleDownload(
                            msg.rawContent!,
                            "docx",
                            `docx-${idx}`,
                            (c, t, tk) => downloadReport(c, t, tk, "docx"),
                          )
                        }
                        disabled={downloading === `docx-${idx}`}
                      >
                        {downloading === `docx-${idx}` ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="mr-2 h-4 w-4" />
                        )}
                        Download DOCX
                      </Button>
                    </div>
                  )}
                  {msg.reviewBatch &&
                    msg.reviewBatch.recordCount > 0 &&
                    onReviewClick && (
                      <Button
                        variant="contained"
                        className="mt-3 w-full"
                        onClick={() =>
                          onReviewClick(
                            msg.reviewBatch!.batchId,
                            msg.reviewBatch!.recordCount,
                          )
                        }
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Review {msg.reviewBatch.recordCount} Record(s)
                      </Button>
                    )}
                  {msg.pendingEmission && onEmissionReviewClick && (
                    <Button
                      variant="contained"
                      className="mt-3 w-full"
                      onClick={() =>
                        onEmissionReviewClick(
                          msg.pendingEmission!.chatId,
                          msg.pendingEmission!.messageId,
                        )
                      }
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      {msg.pendingEmission.status === "claiming"
                        ? "Check emission log status"
                        : "Review emission log"}
                    </Button>
                  )}
                  {msg.summary && onSummaryClick && (
                    <Button
                      variant="outlined"
                      className="mt-2 w-full"
                      onClick={() => onSummaryClick(msg.summary!)}
                    >
                      View Summary ({msg.summary.created} created
                      {msg.summary.failed > 0
                        ? `, ${msg.summary.failed} failed`
                        : ""}
                      )
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {msg.screenContextSent && (
                    <div
                      className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/15 px-2.5 py-1 text-[11px] font-medium text-primary-foreground/90"
                      title={contextualElementsLabel(msg.screenContextSent)}
                      aria-label={`On-screen context: ${contextLabel(msg.screenContextSent)}`}
                    >
                      <span>On screen:</span>
                      <span>{contextLabel(msg.screenContextSent)}</span>
                    </div>
                  )}
                  {msg.content && (
                    <div className="whitespace-pre-wrap break-words">
                      {msg.content}
                    </div>
                  )}
                  {msg.fileAttachments && (
                    <FileBubble
                      files={msg.fileAttachments}
                      tone={msg.role === "user" ? "inverted" : "default"}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        {isThinking && !streamingContent && (
          <ThinkingIndicator currentProgress={currentProgress} />
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Filename dialog for report downloads */}
      <Dialog
        open={saveDialog !== null}
        onClose={() => setSaveDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          Download Report
        </DialogTitle>
        <DialogContent>
          <p className="text-sm text-muted-foreground">
            Choose a filename for your{" "}
            {saveDialog?.ext === "pdf" ? "PDF" : "DOCX"} report.
          </p>
          <div className="mt-4 space-y-2">
            <FieldLabel htmlFor="report-filename">Filename</FieldLabel>
            <TextField
              id="report-filename"
              size="small"
              fullWidth
              value={saveTitle}
              onChange={(e) => setSaveTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmDownload()}
              placeholder="Report"
              autoFocus
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSaveDialog(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmDownload}
            disabled={!saveTitle.trim()}
          >
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});
